import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

export function useApproverStatus() {
  return useQuery({ queryKey: ['stockOrders', 'me'], queryFn: async () => (await api.get('/api/stock-orders/me')).data });
}

export function useMyOrders() {
  return useQuery({
    queryKey: ['stockOrders', 'mine'],
    queryFn: async () => (await api.get('/api/stock-orders/mine')).data.orders,
    staleTime: 0,
  });
}

export function useApprovalQueue(status = 'submitted') {
  return useQuery({
    queryKey: ['stockOrders', 'queue', status],
    queryFn: async () => (await api.get(`/api/stock-orders?status=${status}`)).data.orders,
    staleTime: 0,
  });
}

export function useOrder(id) {
  return useQuery({
    queryKey: ['stockOrders', 'detail', String(id)],
    queryFn: async () => (await api.get(`/api/stock-orders/${id}`)).data,
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/stock-orders', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useSubmitOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/stock-orders/${id}/submit`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useApproveOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, lines }) => (await api.post(`/api/stock-orders/${id}/approve`, { lines })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useRejectOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => (await api.post(`/api/stock-orders/${id}/reject`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useReorderSuggestions() {
  return useQuery({ queryKey: ['stockOrders', 'reorder'], queryFn: async () => (await api.get('/api/stock-orders/reorder-suggestions')).data, staleTime: 0 });
}

export function useStorerooms() {
  return useQuery({ queryKey: ['storerooms'], queryFn: async () => (await api.get('/api/stock-orders/storerooms')).data.storerooms, staleTime: 300000 });
}

export function useSendSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, supplier_id, line_ids, requester, contract, area, comments }) => (await api.post(`/api/stock-orders/${id}/send-supplier`, { supplier_id, line_ids, requester, contract, area, comments })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

export function useTrackingOptions() {
  return useQuery({
    queryKey: ['stockOrders', 'tracking-options'],
    queryFn: async () => (await api.get('/api/stock-orders/xero/tracking-options')).data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/stock-orders/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockOrders'] }),
  });
}

// Consumables that can actually be bought: storeroom items with a real
// supplier price behind them. Items with no price list, or with prices that
// disagree on the unit (per-each vs per-pack), are absent by design.
export function useConsumableCatalogue(q) {
  return useQuery({
    queryKey: ['stockOrders', 'consumableCatalogue', q || ''],
    queryFn: async () => (await api.get(
      `/api/stock-orders/catalogue/consumables${q ? `?q=${encodeURIComponent(q)}` : ''}`)).data.items,
    staleTime: 60000,
  });
}
